// src/components/HelpChat.jsx
import { useState, useRef, useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { getBearerToken } from '../api/kailux'

import Icon from './Icon'

import { T, alpha } from '../theme/theme'
const OPERATOR_EMAIL    = 'support@kailux.co.za'

const SUGGESTED = [
  'How do I upload a trial balance?',
  'Why is my AFS showing no comparatives?',
  'How do I add a new client?',
  'What is the difference between compilation and review?',
  'How do I fix an incorrect account mapping?',
  'My PDF download is failing — what do I do?',
]

// Safety net: the system prompt instructs the model not to use markdown emphasis
// (this chat renders plain text), but LLMs occasionally use it anyway. Strip
// **bold** and ***bold italic*** asterisk markers without touching the text
// inside them. Single-asterisk *italic* is deliberately NOT stripped — accounting
// answers legitimately contain bare asterisks for multiplication (e.g. "cost * rate"),
// and a single-asterisk regex can't tell a real markdown pair from two unrelated
// multiplications in the same message, sometimes eating the text between them.
const stripMarkdown = (text) => {
  if (!text) return text
  return text
    .replace(/\*\*\*(.+?)\*\*\*/g, '$1')
    .replace(/\*\*(.+?)\*\*/g, '$1')
}

export default function HelpChat() {
  const [open,     setOpen]     = useState(false)
  const [messages, setMessages] = useState([])
  const [input,    setInput]    = useState('')
  const [loading,  setLoading]  = useState(false)
  const [showEscalate, setShowEscalate] = useState(false)
  const [unread,   setUnread]   = useState(0)
  // Dedicated "report a problem" path — always available from the moment the
  // chat opens, independent of whether the AI has decided to hand off. Sits
  // alongside the normal Q&A flow, not gated behind it.
  const [reportOpen,    setReportOpen]    = useState(false)
  const [reportText,    setReportText]    = useState('')
  const [reportSending, setReportSending] = useState(false)
  const bottomRef  = useRef(null)
  const inputRef   = useRef(null)
  const { firm }   = useAuth()

  // Greeting on first open
  useEffect(() => {
    if (open && messages.length === 0) {
      setMessages([{
        role: 'assistant',
        text: `Hi${firm?.name ? ` from ${firm.name}` : ''}! I'm the KaiLux assistant. I can help you with anything on the platform — uploading documents, generating AFS, managing clients, billing, and more.\n\nWhat can I help you with today?`,
        ts: Date.now(),
      }])
    }
    if (open) {
      setUnread(0)
      setTimeout(() => inputRef.current?.focus(), 100)
    }
  }, [open])

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, loading])

  const send = async (text) => {
    const userText = (text || input).trim()
    if (!userText || loading) return
    setInput('')
    setShowEscalate(false)
    setReportOpen(false)

    const newMessages = [...messages, { role:'user', text: userText, ts: Date.now() }]
    setMessages(newMessages)
    setLoading(true)

    try {
      const token = await getBearerToken()
      const res = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://kailux-platform-production.up.railway.app'}/api/help/chat`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            messages: newMessages.map(m => ({
              role: m.role,
              content: m.text,
            })),
            firm_name: firm?.name || '',
            firm_seats: firm?.user_count || 1,
          }),
        }
      )

      const data = await res.json()

      if (res.status === 401) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          text: stripMarkdown(data.detail) || 'Your session has expired — please log in again.',
          ts: Date.now(),
          error: true,
        }])
        return
      }

      const reply = stripMarkdown(data.reply) || "I'm not sure about that. Would you like me to connect you with an operator?"

      setMessages(prev => [...prev, {
        role: 'assistant',
        text: reply,
        ts: Date.now(),
      }])

      // Trust the backend's escalate decision — it already applies the
      // genuine handoff-trigger phrase list (billing disputes, outages, etc.)
      if (data.escalate) {
        setShowEscalate(true)
      }

    } catch(err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: "I'm having trouble connecting right now. Please try again or contact our team directly.",
        ts: Date.now(),
        error: true,
      }])
      setShowEscalate(true)
    } finally {
      setLoading(false)
      if (!open) setUnread(u => u + 1)
    }
  }

  const handleKey = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      send()
    }
  }

  const [escalating, setEscalating] = useState(false)
  const [escalateSent, setEscalateSent] = useState(false)

  const escalateOperator = async () => {
    const lastUserMsg = [...messages].reverse().find(m => m.role === 'user')
    setEscalating(true)
    try {
      const token = await getBearerToken()
      const res = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://kailux-platform-production.up.railway.app'}/api/help/escalate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            note: `Help chat escalation from ${firm?.name || 'a firm'}.`,
            last_question: lastUserMsg?.text || '',
          }),
        }
      )
      if (res.ok) {
        setEscalateSent(true)
        setMessages(prev => [...prev, {
          role: 'assistant',
          text: "Done — I've sent this to the KaiLux team and they'll get back to you.",
          ts: Date.now(),
        }])
        setShowEscalate(false)
      } else {
        throw new Error('Escalation request failed')
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: "I couldn't send that through right now — please try email below instead.",
        ts: Date.now(),
        error: true,
      }])
    } finally {
      setEscalating(false)
    }
  }

  const submitReport = async () => {
    const note = reportText.trim()
    if (!note || reportSending) return
    const lastUserMsg = [...messages].reverse().find(m => m.role === 'user')
    setReportSending(true)
    try {
      const token = await getBearerToken()
      const res = await fetch(
        `${import.meta.env.VITE_API_URL || 'https://kailux-platform-production.up.railway.app'}/api/help/escalate`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
          body: JSON.stringify({
            note: `Reported broken via chat: ${note}`,
            last_question: lastUserMsg?.text || '',
          }),
        }
      )
      if (res.ok) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          text: "Thanks — I've flagged this to the KaiLux team and they'll follow up.",
          ts: Date.now(),
        }])
        setReportText('')
        setReportOpen(false)
      } else {
        throw new Error('Report request failed')
      }
    } catch (err) {
      setMessages(prev => [...prev, {
        role: 'assistant',
        text: "I couldn't send that report through right now — please try email below instead.",
        ts: Date.now(),
        error: true,
      }])
      setShowEscalate(true)
      setReportOpen(false)
    } finally {
      setReportSending(false)
    }
  }

  const escalateEmail = () => {
    const lastUserMsg = [...messages].reverse().find(m => m.role === 'user')
    const subject = encodeURIComponent('KaiLux Support Request')
    const body = encodeURIComponent(
      `Hi KaiLux support,\n\nI need help with:\n\n"${lastUserMsg?.text || 'General enquiry'}"\n\nFirm: ${firm?.name || ''}\nSeats: ${firm?.user_count || 1}`
    )
    window.open(`mailto:${OPERATOR_EMAIL}?subject=${subject}&body=${body}`)
  }

  const clearChat = () => {
    setMessages([])
    setShowEscalate(false)
    setReportOpen(false)
    setReportText('')
    // Re-trigger greeting
    setTimeout(() => {
      setMessages([{
        role: 'assistant',
        text: `Hi${firm?.name ? ` from ${firm.name}` : ''}! I'm the KaiLux assistant. What can I help you with today?`,
        ts: Date.now(),
      }])
    }, 100)
  }

  return (
    <>
      {/* ── FLOATING BUTTON ─────────────────────────────────────────────── */}
      <div
        onClick={() => setOpen(v => !v)}
        style={{
          position: 'fixed', bottom: '28px', right: '28px', zIndex: 1000,
          width: '56px', height: '56px', borderRadius: '50%',
          background: T.prg,
          boxShadow: `0 4px 24px ${alpha(T.pri, 0.45)}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          cursor: 'pointer', transition:'all 0.3s',
          transform: open ? 'rotate(45deg) scale(0.9)' : 'scale(1)',
        }}
        onMouseEnter={e => e.currentTarget.style.transform = open ? 'rotate(45deg) scale(0.95)' : 'scale(1.1)'}
        onMouseLeave={e => e.currentTarget.style.transform = open ? 'rotate(45deg) scale(0.9)' : 'scale(1)'}
      >
        {open ? (
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none"
            stroke={T.onpri} strokeWidth="2.5" strokeLinecap="round">
            <line x1="18" y1="6" x2="6" y2="18"/>
            <line x1="6" y1="6" x2="18" y2="18"/>
          </svg>
        ) : (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
            stroke={T.onpri} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
          </svg>
        )}
        {/* Unread badge */}
        {unread > 0 && !open && (
          <div style={{
            position: 'absolute', top: '-4px', right: '-4px',
            width: '20px', height: '20px', borderRadius: '50%',
            background: T.gn, color: T.onpri,
            fontSize:'12px', fontWeight: '800',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontFamily: "'Outfit', sans-serif",
          }}>
            {unread}
          </div>
        )}
      </div>

      {/* ── CHAT PANEL ──────────────────────────────────────────────────── */}
      {open && (
        <div style={{
          position: 'fixed', bottom: '96px', right: '28px', zIndex: 999,
          width: '380px', height: '540px',
          background: T.s1, border: `1px solid ${T.bd}`,
          borderRadius:'20px', display: 'flex', flexDirection: 'column',
          boxShadow:T.shl,
          fontFamily: "'Outfit', sans-serif",
          animation: 'chatSlideUp 0.25s ease',
        }}>

          {/* Header */}
          <div style={{
            padding: '16px 18px', borderBottom: `1px solid ${T.bd}`,
            display: 'flex', alignItems: 'center', gap:'12px',
            flexShrink: 0,
          }}>
            <div style={{
              width: '36px', height: '36px', borderRadius:'8px',
              background: T.prg, color: T.onpri,
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: '16px', flexShrink: 0,
            }}><Icon name="ai" /></div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: '14px', fontWeight: '700', color: T.tx }}>
                KaiLux Assistant
              </div>
              <div style={{ fontSize:'12px', color: T.gn, display: 'flex',
                alignItems: 'center', gap:'5px' }}>
                <div style={{ width: '6px', height: '6px', borderRadius: '50%',
                  background: T.gn }}/>
                Online
              </div>
            </div>
            <button onClick={() => { setReportOpen(v => !v); setShowEscalate(false) }}
              title="Report something that's broken"
              style={{ background: 'none', border: 'none',
                color: reportOpen ? T.or : T.mt,
                cursor: 'pointer', fontSize:'12px', fontFamily: "'Outfit',sans-serif",
                padding: '4px 8px', borderRadius:'6px',
                transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = T.or}
              onMouseLeave={e => e.currentTarget.style.color = reportOpen ? T.or : T.mt}>
              <Icon name="alert" /> Report a problem
            </button>
            <button onClick={clearChat}
              style={{ background: 'none', border: 'none', color: T.mt,
                cursor: 'pointer', fontSize:'12px', fontFamily: "'Outfit',sans-serif",
                padding: '4px 8px', borderRadius:'6px',
                transition: 'color 0.2s' }}
              onMouseEnter={e => e.currentTarget.style.color = T.tx}
              onMouseLeave={e => e.currentTarget.style.color = T.mt}>
              New chat
            </button>
          </div>

          {/* Messages */}
          <div style={{
            flex: 1, overflowY: 'auto', padding:'16px',
            display: 'flex', flexDirection: 'column', gap:'12px',
          }}>
            {messages.map((msg, i) => (
              <div key={i} style={{
                display: 'flex',
                justifyContent: msg.role === 'user' ? 'flex-end' : 'flex-start',
              }}>
                <div style={{
                  maxWidth: '85%',
                  padding: '10px 14px',
                  borderRadius: msg.role === 'user'
                    ? '16px 16px 4px 16px'
                    : '16px 16px 16px 4px',
                  background: msg.role === 'user'
                    ? T.prg
                    : msg.error ? alpha(T.rd, 0.1) : T.s2,
                  border: msg.role === 'assistant'
                    ? `1px solid ${msg.error ? alpha(T.rd, 0.2) : T.bd}`
                    : 'none',
                  fontSize:'14px',
                  color: msg.role === 'user' ? T.onpri : T.tx,
                  lineHeight: '1.6',
                  whiteSpace: 'pre-wrap',
                }}>
                  {msg.text}
                </div>
              </div>
            ))}

            {/* Loading indicator */}
            {loading && (
              <div style={{ display: 'flex', justifyContent: 'flex-start' }}>
                <div style={{
                  padding: '12px 16px', borderRadius: '16px 16px 16px 4px',
                  background: T.s2, border: `1px solid ${T.bd}`,
                  display: 'flex', gap:'5px', alignItems: 'center',
                }}>
                  {[0,1,2].map(i => (
                    <div key={i} style={{
                      width: '7px', height: '7px', borderRadius: '50%',
                      background: T.or, opacity: 0.7,
                      animation: `bounce 1.2s ${i * 0.2}s infinite`,
                    }}/>
                  ))}
                </div>
              </div>
            )}

            {/* Report a problem — always reachable via the header button, independent of the AI */}
            {reportOpen && (
              <div style={{
                padding:'12px', background: T.s3,
                border: `1px solid ${T.bd}`, borderRadius:'12px',
              }}>
                <div style={{ fontSize:'13px', color: T.tx, fontWeight: '600', marginBottom: '6px' }}>
                  Is something broken?
                </div>
                <div style={{ fontSize:'12px', color: T.mt, marginBottom: '10px' }}>
                  Tell us what's going wrong and we'll send it straight to the KaiLux team — this doesn't go through the assistant.
                </div>
                <textarea
                  value={reportText}
                  onChange={e => setReportText(e.target.value)}
                  placeholder="e.g. The PDF download button does nothing when I click it"
                  rows={3}
                  style={{
                    width: '100%', padding: '8px 10px', marginBottom: '8px',
                    background: T.s2, border: `1px solid ${T.bd}`,
                    borderRadius:'8px', color: T.tx,
                    fontSize:'13px', fontFamily: "'Outfit',sans-serif",
                    outline: 'none', resize: 'none', boxSizing: 'border-box',
                  }}
                  onFocus={e => e.target.style.borderColor = T.or}
                  onBlur={e  => e.target.style.borderColor = T.bd}
                />
                <button onClick={submitReport} disabled={reportSending || !reportText.trim()}
                  style={{
                    width: '100%', padding:'8px', borderRadius:'8px', border: 'none',
                    background: T.or, color: T.onpri,
                    fontFamily: "'Outfit',sans-serif", fontSize:'13px',
                    fontWeight: '700',
                    cursor: (reportSending || !reportText.trim()) ? 'default' : 'pointer',
                    opacity: (reportSending || !reportText.trim()) ? 0.6 : 1,
                  }}>
                  {reportSending ? 'Sending…' : <><Icon name="bell" /> Send to the KaiLux team</>}
                </button>
              </div>
            )}

            {/* Escalation options */}
            {showEscalate && !loading && (
              <div style={{
                padding:'12px', background: T.s3,
                border: `1px solid ${T.bd}`, borderRadius:'12px',
              }}>
                <div style={{ fontSize:'13px', color: T.mt, marginBottom: '10px' }}>
                  Would you like to speak with a person?
                </div>
                <button onClick={escalateOperator} disabled={escalating}
                  style={{
                    width: '100%', padding:'8px', borderRadius:'8px', border: 'none',
                    background: T.or, color: T.onpri,
                    fontFamily: "'Outfit',sans-serif", fontSize:'13px',
                    fontWeight: '700', cursor: escalating ? 'default' : 'pointer',
                    opacity: escalating ? 0.7 : 1,
                    marginBottom: '8px',
                  }}>
                  {escalating ? 'Sending…' : <><Icon name="bell" /> Notify the KaiLux team</>}
                </button>
                <button onClick={escalateEmail}
                  style={{
                    width: '100%', padding:'8px', borderRadius:'8px',
                    border: `1px solid ${T.bd}`, background: 'transparent',
                    color: T.tx, fontFamily: "'Outfit',sans-serif",
                    fontSize:'13px', fontWeight: '600', cursor: 'pointer',
                  }}>
                  <Icon name="mail" /> Email
                </button>
              </div>
            )}

            <div ref={bottomRef}/>
          </div>

          {/* Suggested questions — only show at start */}
          {messages.length <= 1 && !loading && (
            <div style={{
              padding: '0 16px 12px', display: 'flex',
              flexWrap: 'wrap', gap:'4px', flexShrink: 0,
            }}>
              {SUGGESTED.slice(0, 3).map((q, i) => (
                <button key={i} onClick={() => send(q)}
                  style={{
                    padding: '5px 10px', borderRadius:'20px',
                    border: `1px solid ${T.bd}`, background: T.s2,
                    color: T.mt, fontFamily: "'Outfit',sans-serif",
                    fontSize:'12px', cursor: 'pointer',
                    transition:'all 0.2s',
                  }}
                  onMouseEnter={e => {
                    e.currentTarget.style.borderColor = T.or
                    e.currentTarget.style.color = T.or
                  }}
                  onMouseLeave={e => {
                    e.currentTarget.style.borderColor = T.bd
                    e.currentTarget.style.color = T.mt
                  }}>
                  {q}
                </button>
              ))}
            </div>
          )}

          {/* Input */}
          <div style={{
            padding: '12px 16px', borderTop: `1px solid ${T.bd}`,
            display: 'flex', gap:'8px', alignItems: 'flex-end', flexShrink: 0,
          }}>
            <textarea
              ref={inputRef}
              value={input}
              onChange={e => setInput(e.target.value)}
              onKeyDown={handleKey}
              placeholder="Ask anything about KaiLux..."
              rows={1}
              style={{
                flex: 1, padding: '10px 12px',
                background: T.s2, border: `1px solid ${T.bd}`,
                borderRadius:'8px', color: T.tx,
                fontSize:'14px', fontFamily: "'Outfit',sans-serif",
                outline: 'none', resize: 'none',
                lineHeight: '1.5', maxHeight: '80px', overflow: 'auto',
                transition:'border 0.2s',
              }}
              onFocus={e => e.target.style.borderColor = T.or}
              onBlur={e  => e.target.style.borderColor = T.bd}
            />
            <button
              onClick={() => send()}
              disabled={loading || !input.trim()}
              style={{
                width: '38px', height: '38px', borderRadius:'8px', border: 'none',
                background: loading || !input.trim()
                  ? T.mt
                  : T.prg,
                color:T.onpri, cursor: loading || !input.trim() ? 'not-allowed' : 'pointer',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0, transition:'all 0.2s',
              }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
                stroke={T.onpri} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13"/>
                <polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
            </button>
          </div>
        </div>
      )}

      <style>{`
        @keyframes chatSlideUp {
          from { opacity: 0; transform: translateY(20px) scale(0.95); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        @keyframes bounce {
          0%, 60%, 100% { transform: translateY(0); }
          30%            { transform: translateY(-6px); }
        }
      `}</style>
    </>
  )
}
